import { FCC, LocaleProvider } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { I18nextProvider } from "react-i18next";
import i18n from "../i18n";
import { useConfig } from "../shared/hooks/useConfig";
import { storage } from "../shared/storage";

interface Props {
	timezone?: string;
	initialLocale?: string;
}

export const PMLocaleProvider: FCC<Props> = ({
	children,
	initialLocale = Intl.DateTimeFormat().resolvedOptions().locale,
	timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}) => {
	const { languageSettings } = useConfig();
	const queryClient = useQueryClient();
	const [initialized, setInitialized] = useState(false);
	const calculatedInitialLocale = useMemo(() => {
		const initialCode = initialLocale.split("-")[0];
		const codeFromStorage = storage.getItem("locale");
		const activeLocale = codeFromStorage || initialCode;
		if (!languageSettings.availableLanguages.includes(activeLocale)) {
			return languageSettings.availableLanguages[0];
		} else {
			return activeLocale;
		}
	}, [initialLocale, languageSettings.availableLanguages]);

	const invalidateAllQueries = useCallback(async () => {
		void queryClient.invalidateQueries({
			predicate: (query) => !query.queryKey.includes("config") && !query.queryKey.includes("config.json"),
		});
		void queryClient.resetQueries({
			queryKey: ["hotel-sections"],
		});
	}, [queryClient]);

	const changeLanguage = useCallback(
		(language: string) => {
			storage.setItem("locale", language);
			if (i18n.language !== language) {
				void i18n.changeLanguage(language);
			}
			document.documentElement.lang = language;
			axios.defaults.headers["Content-Language"] = language;
			void invalidateAllQueries();
		},
		[invalidateAllQueries],
	);

	useEffect(() => {
		axios.defaults.headers["Content-Language"] = calculatedInitialLocale;
		void invalidateAllQueries();
		setInitialized(true);
	}, [calculatedInitialLocale, invalidateAllQueries, queryClient]);

	return (
		<I18nextProvider i18n={i18n}>
			<LocaleProvider initialLanguage={calculatedInitialLocale} initialTimeZone={timezone} onChange={changeLanguage}>
				{initialized && children}
			</LocaleProvider>
		</I18nextProvider>
	);
};
